import { i18n } from 'core/localization';
import { ApplicationState } from 'framework/applicationState';
import { ColorDescriptionType } from 'components/HighchartsWrapper';
import { DesignerStore } from 'controls/designer/designerStore';

import { DataSourceSingleGraphWidget } from './DataSourceSingleGraphWidget';
import { DataSourceSingleGraphEditor } from './editor/DataSourceSingleGraphEditor';
import {
	DataSourceSingleGraphChartType,
	DataSourceSingleGraphDataSourceConfig,
	DataSourceSingleGraphWidgetConfig,
} from './types';

// pre-CV-17428 configs stored a single datasource, chartType and colorSettings directly on the config root
type LegacyDataSourceSingleGraphFields = {
	dataSourceId?: string;
	timeColumn?: string;
	metricColumn?: string;
	chartType?: DataSourceSingleGraphChartType;
	colorSettings?: DataSourceSingleGraphWidgetConfig['dataSources'][number]['colorSettings'];
	// configs saved before the datasources -> dataSources field rename
	datasources?: DataSourceSingleGraphDataSourceConfig[];
};

function defaultColorSettings() {
	return {
		type: ColorDescriptionType.Fixed,
		startColor: '#3388ff',
		finishColor: '#3388ff',
	};
}

function emptyDataSourceRow() {
	return {
		dataSourceId: undefined,
		datasourceName: undefined,
		timeColumn: undefined,
		metricColumn: undefined,
		metricRowName: undefined,
		rowKey: undefined,
		rowKeyValue: undefined,
		chartType: 'line' as DataSourceSingleGraphChartType,
		colorSettings: defaultColorSettings(),
	};
}

export function getWidgetDescription() {
	return {
		form: DataSourceSingleGraphEditor,
		defaultConfig: {
			type: 'data-sources-single-graph',
			title: i18n('Datasource single graph'),
			accountId: ApplicationState.accountId,
			dataSourceSelectorAccountId: null,
			dataSources: [emptyDataSourceRow()],
			decimalsNumber: 0,
			period: undefined,
			hideTimePeriodSelector: true,
		} as DataSourceSingleGraphWidgetConfig,
		minWidth: 400,
		widget: DataSourceSingleGraphWidget,
		fullTitle: i18n('Datasource single graph'),
		preProcessConfig: (config: DataSourceSingleGraphWidgetConfig, store: DesignerStore) => {
			config = JSON.parse(JSON.stringify(config));
			if (config.dataSourceSelectorAccountId === undefined) {
				config.dataSourceSelectorAccountId = config.accountId;
			}
			if (store.config.accountId) {
				config.accountId = store.config.accountId;
			}
			// converting old format (single) to new multiple
			const legacy = config as DataSourceSingleGraphWidgetConfig & LegacyDataSourceSingleGraphFields;
			config.dataSources ??= legacy.datasources;

			if (legacy.dataSourceId && !(config.dataSources ?? []).some((x) => x.dataSourceId)) {
				config.dataSources = [
					{
						dataSourceId: legacy.dataSourceId,
						datasourceName: undefined,
						timeColumn: legacy.timeColumn,
						metricColumn: legacy.metricColumn,
						metricRowName: undefined,
						rowKey: undefined,
						rowKeyValue: undefined,
						chartType: legacy.chartType ?? 'line',
						colorSettings: legacy.colorSettings ?? defaultColorSettings(),
					},
				];
			}
			(config.dataSources ?? []).forEach((row) => {
				row.chartType ??= legacy.chartType ?? 'line';
				row.colorSettings ??= legacy.colorSettings ?? defaultColorSettings();
			});

			delete legacy.dataSourceId;
			delete legacy.timeColumn;
			delete legacy.metricColumn;
			delete legacy.chartType;
			delete legacy.colorSettings;
			delete legacy.datasources;

			return config;
		},
	};
}
